import { useState, useEffect } from 'react'
import Alert from '@cloudscape-design/components/alert'
import Box from '@cloudscape-design/components/box'
import Button from '@cloudscape-design/components/button'
import Cards from '@cloudscape-design/components/cards'
import ColumnLayout from '@cloudscape-design/components/column-layout'
import Input from '@cloudscape-design/components/input'
import Spinner from '@cloudscape-design/components/spinner'
import Table from '@cloudscape-design/components/table'
import SpaceBetween from '@cloudscape-design/components/space-between'
import { useCollection } from '@cloudscape-design/collection-hooks'
import { useKeywordRankings } from '@/hooks/useKeywordRankings'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import type { KeywordRanking } from '@/lib/types'
import AIAnnotation from './AIAnnotation'
import { InsightBullet } from '../../../lib/types'

interface Props {
  clientId: string | null
  locationId: string | null
  startDate: string | undefined
  endDate: string | undefined
  insights?: InsightBullet[]
}

const COLUMNS = [
  { id: 'keyword', header: 'Keyword', cell: (r: KeywordRanking) => r.keyword, sortingField: 'keyword' },
  { id: 'position', header: 'Pos.', cell: (r: KeywordRanking) => r.position.toFixed(1), sortingField: 'position', width: 70 },
  { id: 'clicks', header: 'Clicks', cell: (r: KeywordRanking) => r.clicks, sortingField: 'clicks', width: 80 },
  { id: 'impressions', header: 'Impr.', cell: (r: KeywordRanking) => r.impressions, sortingField: 'impressions', width: 80 },
  { id: 'ctr', header: 'CTR', cell: (r: KeywordRanking) => `${(r.ctr * 100).toFixed(1)}%`, sortingField: 'ctr', width: 70 },
]

export default function KeywordRankingsWidget({ clientId, locationId, startDate, endDate, insights }: Props) {
  const [keywordsInput, setKeywordsInput] = useState('')
  const [debouncedKeywords, setDebouncedKeywords] = useState<string[]>([])
  const isMobile = useMediaQuery('(max-width: 1023px)')

  useEffect(() => {
    const timer = setTimeout(() => {
      const parsed = keywordsInput.split(',').map(k => k.trim()).filter(Boolean)
      setDebouncedKeywords(parsed)
    }, 500)
    return () => clearTimeout(timer)
  }, [keywordsInput])

  const { rankings, loading } = useKeywordRankings(clientId, locationId, {
    keywords: debouncedKeywords.length > 0 ? debouncedKeywords : undefined,
    startDate,
    endDate,
  })

  const positionColumn = COLUMNS.find(c => c.id === 'position') ?? COLUMNS[0]
  const { items, collectionProps } = useCollection(rankings, {
    sorting: { defaultState: { sortingColumn: positionColumn, isDescending: false } },
  })

  if (isMobile) {
    return (
      <SpaceBetween size="m">
        <SpaceBetween direction="horizontal" size="xs">
          <Input
            value={keywordsInput}
            onChange={({ detail }) => setKeywordsInput(detail.value)}
            placeholder="Add keywords, comma-separated"
            ariaLabel="Enter keywords to filter rankings"
          />
          <Button onClick={() => setKeywordsInput('')}>Clear</Button>
        </SpaceBetween>
        {loading ? (
          <Box textAlign="center" padding="xl">
            <Spinner size="large" nativeAttributes={{ 'aria-label': 'Loading' }} />
          </Box>
        ) : rankings.length === 0 && debouncedKeywords.length === 0 ? (
          <Alert statusIconAriaLabel="Info" header="Connect Google">
            Connect Search Console in client settings to see keyword rankings.
          </Alert>
        ) : (
          <Cards
            {...collectionProps}
            items={items}
            cardsPerRow={[{ cards: 1 }]}
            cardDefinition={{
              header: (item: KeywordRanking) => (
                <Box variant="strong">{item.keyword}</Box>
              ),
              sections: [
                {
                  id: 'metrics',
                  header: 'Metrics',
                  content: (item: KeywordRanking) => (
                    <ColumnLayout columns={2} variant="text-grid">
                      <SpaceBetween size="xxs">
                        <Box variant="awsui-key-label">Position</Box>
                        <Box>{item.position.toFixed(1)}</Box>
                      </SpaceBetween>
                      <SpaceBetween size="xxs">
                        <Box variant="awsui-key-label">CTR</Box>
                        <Box>{`${(item.ctr * 100).toFixed(1)}%`}</Box>
                      </SpaceBetween>
                      <SpaceBetween size="xxs">
                        <Box variant="awsui-key-label">Clicks</Box>
                        <Box>{item.clicks}</Box>
                      </SpaceBetween>
                      <SpaceBetween size="xxs">
                        <Box variant="awsui-key-label">Impressions</Box>
                        <Box>{item.impressions}</Box>
                      </SpaceBetween>
                    </ColumnLayout>
                  ),
                },
              ],
            }}
            empty={<Box color="text-body-secondary">No rankings found for these keywords</Box>}
          />
        )}
        <AIAnnotation bullets={insights ?? []} />
      </SpaceBetween>
    )
  }

  return (
    <SpaceBetween size="s">
      <SpaceBetween direction="horizontal" size="xs">
        <Input
          value={keywordsInput}
          onChange={({ detail }) => setKeywordsInput(detail.value)}
          placeholder="Add keywords, comma-separated"
          ariaLabel="Enter keywords to filter rankings"
        />
        <Button onClick={() => setKeywordsInput('')}>Clear</Button>
      </SpaceBetween>
      {loading ? (
        <Box textAlign="center" padding="xl">
          <Spinner size="large" nativeAttributes={{ 'aria-label': 'Loading' }} />
        </Box>
      ) : rankings.length === 0 && debouncedKeywords.length === 0 ? (
        <Alert statusIconAriaLabel="Info" header="Connect Google">
          Connect Search Console in client settings to see keyword rankings.
        </Alert>
      ) : (
        <Table
          {...collectionProps}
          items={items}
          columnDefinitions={COLUMNS}
          variant="embedded"
          stripedRows
          empty={<Box color="text-body-secondary">No rankings found for these keywords</Box>}
        />
      )}
      <AIAnnotation bullets={insights ?? []} />
    </SpaceBetween>
  )
}
